<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactJourneyEventsAPI from 'dashboard/api/contactJourneyEvents';
import {
  buildJornadaSummary,
  buildLocalizedTitle,
  formatJourneyDateTime,
  formatJourneyMoney,
  localizedJourneyStatus,
  mergeJornadaEvents,
  normalizeJornadaEvents,
} from './jornada360Adapter';
import Jornada360Drawer from './Jornada360Drawer.vue';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
  /** Modest initial page; further pages load via cursor from the drawer. */
  limit: {
    type: Number,
    default: 40,
  },
  /** Existing lead score when already available from props/endpoints */
  score: {
    type: [Number, String],
    default: null,
  },
  /** When false, renders drawer inline instead of teleporting to body (test-friendly). */
  teleport: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },

  /** Existing funnel/pipeline stage when already available */
  stage: {
    type: String,
    default: null,
  },
});

const { t, locale } = useI18n();

const items = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const error = ref('');
const isDrawerOpen = ref(false);
const nextCursor = ref(null);
const hasMore = ref(false);
let activeFetchRequestId = 0;
let activeLoadMoreRequestId = 0;

const resolvedContactId = computed(() => props.contactId || props.contact?.id);
const contactName = computed(
  () => props.contact?.name || props.contact?.display_name || ''
);

const resolvedScore = computed(() => {
  if (props.score != null && props.score !== '') return props.score;
  const attrs = props.contact?.custom_attributes || {};
  const additional = props.contact?.additional_attributes || {};
  return (
    attrs.lead_score ??
    attrs.score ??
    additional.lead_score ??
    additional.score ??
    null
  );
});

const resolvedStage = computed(() => {
  if (props.stage) return props.stage;
  const attrs = props.contact?.custom_attributes || {};
  const additional = props.contact?.additional_attributes || {};
  return (
    attrs.stage ||
    attrs.funnel_stage ||
    attrs.pipeline_stage ||
    additional.stage ||
    additional.funnel_stage ||
    null
  );
});

const summary = computed(() =>
  buildJornadaSummary(items.value, {
    score: resolvedScore.value,
    stage: resolvedStage.value,
  })
);

const previewItems = computed(() => items.value.slice(0, 3));

// SiteUp: o rodapé vira botão. Com mais páginas no servidor não sabemos
// quantos eventos faltam, então o texto não mostra número.
const hiddenCount = computed(
  () => items.value.length - previewItems.value.length
);
const showFullJourneyButton = computed(
  () => !props.showHeader || hiddenCount.value > 0 || hasMore.value
);
const fullJourneyLabel = computed(() => {
  if (hasMore.value) return t('JORNADA_360.ACTIONS.VIEW_MORE');
  if (hiddenCount.value > 0)
    return t('JORNADA_360.ACTIONS.VIEW_FULL_COUNT', {
      count: hiddenCount.value,
    });
  return t('JORNADA_360.ACTIONS.VIEW_FULL');
});

const formatDate = value => {
  if (!value) return t('AUTOMATION_EVENTS.EMPTY_VALUE');
  return (
    formatJourneyDateTime(value, locale.value, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }) || t('AUTOMATION_EVENTS.EMPTY_VALUE')
  );
};

const formatMoney = (cents, currency = 'BRL') =>
  formatJourneyMoney(cents, currency, locale.value);

const statusLabel = status => localizedJourneyStatus(status, t);

const localizedTitle = item => buildLocalizedTitle(item, t, locale.value);

const statusClass = status => {
  const classes = {
    completed: 'bg-n-teal-3 text-n-teal-11',
    success: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    manual_action: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-blue-3 text-n-blue-11',
    running: 'bg-n-blue-3 text-n-blue-11',
    active: 'bg-n-blue-3 text-n-blue-11',
    ignored: 'bg-n-slate-3 text-n-slate-11',
    skipped: 'bg-n-slate-3 text-n-slate-11',
    received: 'bg-n-slate-3 text-n-slate-11',
    pending: 'bg-n-slate-3 text-n-slate-11',
    cancelled: 'bg-n-slate-3 text-n-slate-11',
    unknown: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || classes.received;
};

const applyPageMeta = meta => {
  nextCursor.value = meta?.next_cursor || null;
  hasMore.value = Boolean(meta?.has_more);
};

/** Wipe journey state so a previous contact can never leak into the card. */
const clearJourneyState = () => {
  items.value = [];
  nextCursor.value = null;
  hasMore.value = false;
  error.value = '';
  isLoadingMore.value = false;
};

/**
 * @param {{ preserveItems?: boolean }} [options]
 * preserveItems=true keeps current rows visible during same-contact refresh.
 * Contact switches always clear first (see watch below).
 */
const fetchEvents = async ({ preserveItems = false } = {}) => {
  activeFetchRequestId += 1;
  const requestId = activeFetchRequestId;
  // Invalidate in-flight load-more from a previous contact/page.
  activeLoadMoreRequestId += 1;
  const contactId = resolvedContactId.value;

  if (!contactId) {
    clearJourneyState();
    isLoading.value = false;
    return;
  }

  if (!preserveItems) {
    clearJourneyState();
  } else {
    // Same-contact refresh: drop cursor/meta so load-more cannot append stale pages.
    nextCursor.value = null;
    hasMore.value = false;
    isLoadingMore.value = false;
    error.value = '';
  }

  isLoading.value = true;

  try {
    const { data } = await ContactJourneyEventsAPI.list(contactId, {
      limit: props.limit,
    });
    if (requestId !== activeFetchRequestId) return;

    items.value = normalizeJornadaEvents(data?.data || []);
    applyPageMeta(data?.meta);
  } catch {
    if (requestId !== activeFetchRequestId) return;
    items.value = [];
    nextCursor.value = null;
    hasMore.value = false;
    error.value = t('JORNADA_360.ERROR');
  } finally {
    if (requestId === activeFetchRequestId) isLoading.value = false;
  }
};

const loadMoreEvents = async () => {
  const contactId = resolvedContactId.value;
  if (!contactId || !hasMore.value || !nextCursor.value || isLoadingMore.value)
    return;

  activeLoadMoreRequestId += 1;
  const requestId = activeLoadMoreRequestId;
  isLoadingMore.value = true;
  error.value = '';

  try {
    const { data } = await ContactJourneyEventsAPI.list(contactId, {
      limit: props.limit,
      cursor: nextCursor.value,
    });
    if (requestId !== activeLoadMoreRequestId) return;

    const pageItems = normalizeJornadaEvents(data?.data || []);
    items.value = mergeJornadaEvents(items.value, pageItems);
    applyPageMeta(data?.meta);
  } catch {
    if (requestId !== activeLoadMoreRequestId) return;
    error.value = t('JORNADA_360.ERROR');
  } finally {
    if (requestId === activeLoadMoreRequestId) isLoadingMore.value = false;
  }
};

const openDrawer = () => {
  isDrawerOpen.value = true;
};

const refreshEvents = () => fetchEvents({ preserveItems: true });

onMounted(() => fetchEvents({ preserveItems: false }));

watch(
  () => resolvedContactId.value,
  () => {
    isDrawerOpen.value = false;
    // Clear before awaiting so the compact card never shows the previous lead.
    clearJourneyState();
    fetchEvents({ preserveItems: false });
  }
);

defineExpose({
  fetchEvents,
  loadMoreEvents,
  items,
  hasMore,
  nextCursor,
  isLoadingMore,
  error,
});
</script>

<template>
  <section
    class="mx-2 mb-2 rounded-xl border border-n-weak bg-n-solid-1"
    data-testid="jornada-360-compact"
  >
    <header
      v-if="showHeader"
      class="flex items-center justify-between gap-3 px-3 py-2.5"
    >
      <div class="flex min-w-0 items-center gap-2">
        <span class="i-ri-planet-line size-4 shrink-0 text-n-brand" />
        <h3 class="truncate text-sm font-medium text-n-slate-12">
          {{ t('JORNADA_360.TITLE') }}
        </h3>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2"
          :aria-label="t('AUTOMATION_EVENTS.ACTIONS.REFRESH')"
          data-testid="jornada-360-refresh"
          @click="refreshEvents"
        >
          <span class="i-ri-refresh-line size-3.5" />
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-md bg-n-brand/10 px-2 py-1 text-[11px] font-medium text-n-brand hover:bg-n-brand/15"
          data-testid="jornada-360-open"
          @click="openDrawer"
        >
          {{ t('JORNADA_360.ACTIONS.OPEN') }}
          <span class="i-ri-layout-right-line size-3.5" />
        </button>
      </div>
    </header>

    <div
      v-if="
        !isLoading && (summary.score != null || summary.stage || summary.value)
      "
      class="flex flex-wrap gap-1.5 border-t border-n-weak px-3 py-2"
    >
      <span
        v-if="summary.score != null"
        class="rounded-md bg-n-blue-3 px-2 py-0.5 text-[11px] font-medium text-n-blue-11"
        data-testid="jornada-360-score"
      >
        {{ t('JORNADA_360.SUMMARY.SCORE', { score: summary.score }) }}
      </span>
      <span
        v-if="summary.stage"
        class="rounded-md bg-n-amber-3 px-2 py-0.5 text-[11px] font-medium text-n-amber-11"
        data-testid="jornada-360-stage"
      >
        {{ t('JORNADA_360.SUMMARY.STAGE', { stage: summary.stage }) }}
      </span>
      <span
        v-if="summary.value"
        class="rounded-md bg-n-teal-3 px-2 py-0.5 text-[11px] font-medium text-n-teal-11"
        data-testid="jornada-360-value"
      >
        {{
          t('JORNADA_360.SUMMARY.VALUE', {
            value: formatMoney(
              summary.value.totalCents,
              summary.value.currency
            ),
          })
        }}
      </span>
    </div>

    <div v-if="isLoading" class="space-y-2 border-t border-n-weak px-3 py-3">
      <div
        v-for="index in 2"
        :key="index"
        class="h-12 animate-pulse rounded-md bg-n-alpha-2"
      />
    </div>

    <div
      v-else-if="error && !items.length"
      role="alert"
      class="flex flex-col items-start gap-2 border-t border-n-weak px-3 py-3"
    >
      <p class="m-0 text-xs text-n-ruby-11">{{ error }}</p>
      <button
        type="button"
        class="inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        data-testid="jornada-360-retry"
        @click="refreshEvents"
      >
        <span class="i-ri-refresh-line size-3.5" />
        {{ t('JORNADA_360.ACTIONS.RETRY') }}
      </button>
    </div>

    <div
      v-else-if="!resolvedContactId"
      class="border-t border-n-weak px-3 py-3 text-xs text-n-slate-10"
    >
      {{ t('AUTOMATION_EVENTS.TIMELINE.NO_CONTACT_KEY') }}
    </div>

    <div
      v-else-if="!items.length"
      class="border-t border-n-weak px-3 py-3 text-xs text-n-slate-10"
    >
      {{ t('JORNADA_360.EMPTY') }}
    </div>

    <div v-else class="border-t border-n-weak">
      <p v-if="error" class="px-3 pt-2 text-xs text-n-amber-11">
        {{ error }}
      </p>
      <button
        v-for="item in previewItems"
        :key="item.id"
        type="button"
        class="flex w-full items-start justify-between gap-2 border-b border-n-weak px-3 py-2.5 text-left last:border-0 hover:bg-n-alpha-2"
        @click="openDrawer"
      >
        <div class="min-w-0">
          <div class="truncate text-xs font-medium text-n-slate-12">
            {{ localizedTitle(item) }}
          </div>
          <div class="mt-0.5 text-[11px] text-n-slate-10">
            {{
              item.product
                ? t('JORNADA_360.META.DATE_PRODUCT', {
                    date: formatDate(item.occurredAt),
                    product: item.product,
                  })
                : formatDate(item.occurredAt)
            }}
          </div>
        </div>
        <span
          class="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium"
          :class="statusClass(item.status)"
        >
          {{ statusLabel(item.status) }}
        </span>
      </button>
      <button
        v-if="showFullJourneyButton"
        type="button"
        class="flex min-h-11 w-full items-center justify-between gap-2 rounded-b-xl px-3 text-left text-xs font-medium text-n-brand hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
        data-testid="jornada-360-view-full"
        @click="openDrawer"
      >
        {{ fullJourneyLabel }}
        <span class="i-ri-layout-right-line size-3.5 shrink-0" />
      </button>
    </div>

    <Jornada360Drawer
      v-model="isDrawerOpen"
      :items="items"
      :is-loading="isLoading"
      :is-loading-more="isLoadingMore"
      :has-more-remote="hasMore"
      :error="error"
      :contact-name="contactName"
      :teleport="teleport"
      :summary="summary"
      @refresh="refreshEvents"
      @load-more="loadMoreEvents"
    />
  </section>
</template>
